import React, { Component } from "react";
import PropTypes from "prop-types";

import moment from "moment";
import { find, isObject, groupBy, orderBy, cloneDeep } from "lodash";

import Loading from "../../common/Loading";
import ConfirmModal from "../../common/ConfirmModal";

import TimesheetList from "./TimesheetList";
import TimesheetFooter from "./TimesheetFooter";
import TimesheetHeader from "./TimesheetHeader";
import TimesheetTotals from "./TimesheetTotals";

/** Modals */
import CallDetailsModal from "./CallDetailsModal";
import ProjectDetailsModal from "./ProjectDetailsModal";

import UserService from "../../../services/userService";
import { customToast as toast, escapeHTML } from "../../../utils";

import { getServiceOptions as getServiceOptionsUtil } from "../utils";

import { generateUniqueId } from "@redkeep/utils";

class TimesheetForm extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      rows: [],
      callId: "",
      rowIndex: "",
      project: {},
      call: {},
      contract: {},
      service: "",
      details: new Map(),
      saving: false,
      emptyRow: [
        { day: "mon" },
        { day: "tue" },
        { day: "wed" },
        { day: "thu" },
        { day: "fri" },
        { day: "sat" },
        { day: "sun" }
      ]
    };
  }

  onEnterCallDetails = (event, row_num, supportTicketId = null) => {
    event.preventDefault();
    const { rows } = this.state;
    const updatedRows = [...rows];

    const { customers, projects, contracts } = this.props;
    const field = event.target.name;

    const row = updatedRows[row_num];
    const call = find(row.calls, { day: field });
    this.props.updateCall(call);
    if (!call.stage || call.stage.order <= 4) {
      const customer = customers.find(
        (cust) => cust._id === row[`customer ${row_num}`]
      );

      const project = row[`project ${row_num}`]
        ? projects.find((proj) => proj._id === row[`project ${row_num}`]._id)
        : undefined;

      const isProjectReadyToClose =
        project.project_status?.label === "Ready To Close";

      if (isProjectReadyToClose && !call?._id) {
        this.handleShowReadyToCloseModal();
        this.setState({ ...this.state, project });
        return null;
      }

      const contract = row[`project ${row_num}`]
        ? find(contracts, {
            _id: isObject(row[`project ${row_num}`].contract)
              ? row[`project ${row_num}`].contract._id
              : row[`project ${row_num}`].contract
          })
        : undefined;
      const service = row[`service ${row_num}`] || "";
      call.support_ticket_id = supportTicketId;
      this.setState(
        {
          field: `${field} ${row_num}`,
          call,
          customer,
          project,
          contract,
          service,
          rows: updatedRows
        },
        () => this.props.onEnterCallDetails()
      );
    }
  };

  onChange = (field, selected) => {
    const { rows } = this.state;
    const { projects } = this.props;

    const idx = field.split(" ")[1];
    const row = { ...rows[idx], [field]: selected.value };

    const { calls } = row;

    calls.forEach((call) => {
      if (call._id) {
        call.changed = true;
      }
    });

    /* If customer field is changed, clear project */
    if (field === `customer ${idx}`) {
      delete row[`project ${idx}`];
    }

    if (field === `project ${idx}`) {
      const project = projects.find((p) => p._id === selected.value);
      row[field] = project;
      calls.forEach((call) => {
        if (call._id || call.actual_hrs) {
          call.changed = true;
          call.call_type = project.call_type;
        }
      });
    }

    // Update all row calls if project was changed
    if (
      rows[idx][`project ${idx}`]?._id !== row[`project ${idx}`]?._id &&
      row[`project ${idx}`]?._id &&
      calls.find((call) => call._id)
    ) {
      this.handleSaveRowChanges(row, idx);
    }

    rows[idx] = row;

    this.setState({ rows: [...rows] });
  };

  onChangeInput = (dow, hrs, row_num, clear = false) => {
    const { dates } = this.props;
    const { rows } = this.state;
    const updatedRows = [...rows];

    const date = dates.find((d) => d.day === dow)?.date;
    const idx = dates.findIndex((d) => d.day === dow);

    const actualHours = !Number.isNaN(hrs) && hrs >= 0 ? hrs : "";
    const isSupportTicket = updatedRows[row_num].calls.some(
      (item) => item.support_ticket_id
    );
    // add support ticket number if call is on a ticket
    if (isSupportTicket) {
      const ticketId = updatedRows[row_num].calls.find(
        (item) => typeof item.support_ticket_id === "string"
      ).support_ticket_id;

      updatedRows[row_num].calls[idx] = {
        ...updatedRows[row_num].calls[idx],
        support_ticket_id: ticketId
      };
    }

    updatedRows[row_num].calls[idx] = clear
      ? { day: dow }
      : {
          ...updatedRows[row_num].calls[idx],
          row_num,
          date,
          actual_hrs: actualHours,
          changed: true,
          error: this.getErrorStatus(
            updatedRows[row_num].calls[idx],
            actualHours
          )
        };

    const total = updatedRows[row_num].calls
      .filter((item) => item.actual_hrs)
      .reduce((acc, curr) => acc + curr.actual_hrs, 0);

    updatedRows[row_num][`total ${row_num}`] = total;
    this.setState({ rows: updatedRows });
  };

  getProjectDetails = (project) => {
    this.setState({ project }, () => this.props.onShowProjectDetails());
  };

  getErrorStatus = (call, hours) => {
    if (!hours) {
      return !call.internal_notes;
    }
    return !call.billing_description || !call.internal_notes;
  };

  UNSAFE_componentWillReceiveProps = (nextProps) => {
    if (nextProps.calls !== this.props.calls) {
      this.buildData(nextProps.calls);
    }
  };

  handleSaveSingleRow = async (rowIndex) => {
    try {
      this.setState({ saving: true });
      const { codes, onSave, user } = this.props;
      const { rows } = this.state;
      const row = rows[rowIndex];

      const customer = row[`customer ${rowIndex}`];
      const project = row[`project ${rowIndex}`];
      const service = row[`service ${rowIndex}`];
      if (
        !(!customer && !project && !service) &&
        (!customer || !project || !service)
      ) {
        throw new Error(
          "Please be sure that a customer, project, and service is selected."
        );
      }

      const defaultWorkplace = find(codes, { label: "Office" });
      const { department } = await UserService.getUser(user);
      const completedCallStatus = find(codes, {
        entity: "Call",
        field: "status",
        label: "Completed"
      });
      const reservedCallStatus = find(codes, {
        entity: "Call",
        field: "status",
        label: "Reserved"
      });

      const ticketID = row.calls.find(
        (c) => c.support_ticket_id
      )?.support_ticket_id;

      const calls = row.calls
        .filter((call) => call.actual_hrs >= 0 || call._id)
        .map((call) => {
          const { day } = call;
          const { date } = find(this.props.dates, { day });

          const call_type = find(codes, {
            _id: call.call_type?._id || call.call_type || project?.call_type._id
          });

          let callStatus = completedCallStatus;

          if (
            !call.actual_hrs ||
            call.actual_hrs === "0" ||
            parseFloat(call.actual_hrs, 10) === 0
          ) {
            callStatus = reservedCallStatus;
          }

          const contract = this.props.contracts.find(
            (c) => c._id === project.contract || c._id === project.contract?._id
          );
          const contractType = contract?.contract_type?.value;
          let billable_hrs = 0;
          if (call_type && call_type.is_billable) {
            switch (contractType) {
              case "FIXED FEE":
              case "RETAINER":
              case "CLIENTCARE":
              case "CAPITAL":
              case "MANAGED SERVICES":
                billable_hrs = call.actual_hrs;
                break;
              default:
                billable_hrs = Math.ceil(call.actual_hrs);
                break;
            }
          }
          return {
            ...call,
            customer,
            project: project._id,
            contract: project.contract || null,
            service: call.tasks?.[0]?.service || service,
            department,
            recurring_id: null,
            arrival_date: call.arrival_date
              ? call.arrival_date
              : moment(date).add(8, "hours"),
            completed_date: call.arrival_date
              ? moment(call.arrival_date).add(call.actual_hrs || 0.5, "hours")
              : moment(date).add(
                  parseFloat(call.actual_hrs || 0.5) + 8,
                  "hours"
                ),
            assigned_to: user,
            stage: callStatus._id,
            actual_hrs: call.actual_hrs,
            billable_hrs: billable_hrs || 0,
            call_type: call_type?._id,
            workplace: call.workplace || defaultWorkplace._id
          };
        });
      const callsToSave = calls.map((call) =>
        call.actual_hrs !== null && !Number.isNaN(call.actual_hrs)
          ? { ...call }
          : { ...call, actual_hrs: "" }
      );
      if (callsToSave.some((call) => call.actual_hrs > 24)) {
        throw new Error("Daily calls cannot exceed 24 hours");
      }
      this.setState({ details: new Map() });
      this.props.closeModal();
      await onSave(callsToSave, ticketID);
    } catch (err) {
      toast.error(err.message);
    } finally {
      this.setState({ saving: false });
    }
  };

  handleSaveDetails = (call) => {
    const { field, rows } = this.state;
    const updatedRows = [...rows];
    const tasksWithMinutes = call.tasks?.map(
      ({ actual_duration, duration_units }) =>
        (duration_units.value || duration_units) === "Minutes"
          ? actual_duration / 60
          : actual_duration
    );
    const tasksHours = tasksWithMinutes.length
      ? tasksWithMinutes.reduce(
          (total, task) => Number(total) + Number(parseFloat(task) || 0)
        ) || 0
      : 0;
    const [day, strIdx] = field.split(" ");

    const row = updatedRows[parseInt(strIdx)];
    call.changed = true;
    const d = row.calls.findIndex((x) => x.day === day);
    row.calls[d] = {
      ...row.calls[d],
      ...call,
      actual_hrs: Number(tasksHours).toFixed(2),
      error: false
    };

    const { details } = this.state;
    details.set(field, call);

    this.setState(
      {
        details,
        rows: updatedRows
      },
      () => this.handleSaveSingleRow(parseInt(strIdx))
    );
  };

  handleSaveRowChanges = (row, idx) => {
    const rows = [...this.state.rows];

    const calls = row.calls.map((call) => {
      if (call.actual_hrs >= 0 || call._id) {
        return {
          ...call,
          customer: row[`customer ${idx}`],
          project: row[`project ${idx}`]
        };
      }
      return call;
    });

    row.calls = calls;

    rows[parseInt(idx)] = row;

    this.setState({ rows }, () => this.handleSaveSingleRow(idx));
  };

  handleDeleteCall = async () => {
    const { rows, field } = this.state;
    const [day, strIdx] = field.split(" ");

    const updatedRows = [...rows];
    const row = updatedRows[parseInt(strIdx)];
    const d = row.calls.findIndex((x) => x.day === day);
    row.calls[d] = {
      day
    };
    await this.props.deleteCall(this.state.callId);
    await this.props.closeModal();
    const rowsToBuild = [];
    updatedRows.forEach((updated) => {
      updated.calls.forEach((call) => {
        if (call._id) {
          rowsToBuild.push(call);
        }
      });
    });
    this.buildData(rowsToBuild);
    await this.props.onSave([]);
  };

  handleModalDeleteCall = (callId) => {
    this.setState({ callId });
    this.props.showModalSuccess("callConfirmModal");
  };

  closeProjectDetails = () => {
    this.props.closeModal();
    this.setState({ project: {} });
  };

  buildData = (data) => {
    this.setState({ loading: true });

    let rows = [];

    const groupCalls = (calls) =>
      groupBy(
        orderBy(calls, [
          "customer.name",
          "project.name",
          "service.description",
          "support_ticket_id"
        ]),
        (call) => {
          if (call?.project?.name) {
            if (call.service && call.customer?.name) {
              if (call.support_ticket_id) {
                return `${call.customer.name} - ${call.project.name} - ${call.service.description} - ${call.support_ticket_id}`;
              }
              return `${call.customer.name} - ${call.project.name} - ${call.service.description}`;
            }
            return `${call?.customer?.name} - ${call?.project?.name}`;
          }
          return `${call?.customer?.name || ""}`;
        }
      );

    if (data.length) {
      /* Group calls by customer, project, and service */
      const groupedCalls = groupCalls(data);

      /* Each group will represent 1 row; some groups may end up having calls that share the same cell in a row (same grouping + same date); 
      Organize grouped calls into separate rows (if necessary); Ultimately, each cell should represent 1 call; */
      const buildUniqRows = (grpCalls) => {
        Object.values(grpCalls).forEach((group) => {
          const newRow = [];
          // loop through calls in group
          const calls = group.reduce((arr, call) => {
            const thisCall = call;

            const noExist = !arr.some((item) =>
              moment(item.arrival_date).isSame(thisCall.arrival_date, "date")
            );

            const rowTicketId =
              arr.find((item) => typeof item.support_ticket_id === "string")
                ?.support_ticket_id || false;
            let ticketConflict;
            // check ticket conflict
            if (thisCall.support_ticket_id) {
              if (rowTicketId) {
                ticketConflict = thisCall.support_ticket_id !== rowTicketId;
              } else {
                ticketConflict = !!arr.find((item) => item._id);
              }
            } else {
              ticketConflict = !!rowTicketId;
            }

            /* if call doesn't have date or ticket conflict in current row, push to that row
            otherwise push to another array and recursively check for date conflicts in next row */
            if (noExist && !ticketConflict) {
              arr.push(call);
            } else {
              newRow.push(call);
            }

            return arr;
          }, []);

          rows.push({ id: generateUniqueId(), calls });

          const newGroup = groupCalls(newRow);
          if (Object.values(newGroup).length) buildUniqRows(newGroup);
        });
      };

      buildUniqRows(groupedCalls);

      rows.forEach((row, row_num) => {
        let total = 0;
        const calls = cloneDeep(this.state.emptyRow);
        row.calls.forEach((call) => {
          row[`customer ${row_num}`] = call.customer?._id;
          row[`project ${row_num}`] = call.project; // whole project object is used for progress component
          row[`service ${row_num}`] = call.service && call.service._id;
          total += call.actual_hrs;
          row[`total ${row_num}`] = total;

          const date = moment(call.arrival_date);

          const day = date.day() > 0 ? date.day() : 7;

          calls[day - 1] = { ...calls[day - 1], ...call };

          row.calls = calls;
        });
      });
    } else {
      rows = [{ id: generateUniqueId(), calls: [...this.state.emptyRow] }];
    }

    this.setState({ rows: [...rows], loading: false });
  };

  addRow = () => {
    const { emptyRow, rows } = this.state;
    this.setState({
      rows: [...rows, { id: generateUniqueId(), calls: [...emptyRow] }]
    });
  };

  handleShowReadyToCloseModal = () => {
    this.props.showModalSuccess("closeProjectModal");
  };

  getServiceOptions = (customer, project) =>
    getServiceOptionsUtil({
      project,
      customer,
      users: this.props.users,
      user: this.props.user,
      customers: this.props.customers,
      projects: this.props.projects,
      services: this.props.services
    });

  render() {
    const {
      customers,
      projects,
      contracts,
      auth,
      dates,
      codes,
      services,
      user,
      users,
      closeModal
    } = this.props;

    const {
      rows,
      loading,
      project,
      call,
      customer,
      contract,
      service,
      saving
    } = this.state;

    const confirmCloseProject = (
      <div>
        <p>
          {escapeHTML(
            "This project's status has been changed to Ready To Close. No additional calls can be added."
          )}
        </p>
      </div>
    );

    return (
      <React.Fragment>
        <form method="POST" className="time-sheet form">
          <TimesheetHeader dates={dates} auth={auth} />
          {loading && (
            <div className="flex -center -full">
              <Loading active />
            </div>
          )}
          {!loading && rows && Array.isArray(rows) && rows.length > 0 && (
            <React.Fragment>
              <TimesheetTotals rows={rows} dates={dates} />
              <TimesheetList
                rows={rows}
                customers={customers}
                projects={projects}
                contracts={contracts}
                auth={auth}
                services={services}
                user={user}
                users={users}
                onChange={this.onChange}
                getServiceOptions={this.getServiceOptions}
                onChangeInput={this.onChangeInput}
                onEnterCallDetails={this.onEnterCallDetails}
                onShowProjectDetails={this.getProjectDetails}
              />
            </React.Fragment>
          )}

          <TimesheetFooter addRow={this.addRow} />
        </form>
        <CallDetailsModal
          call={call}
          codes={codes}
          project={project}
          customer={customer}
          contract={contract}
          service={service}
          handleSave={this.handleSaveDetails}
          handleDelete={this.handleModalDeleteCall}
          handleCancel={closeModal}
          auth={auth}
          dates={this.props.dates}
          saving={saving}
          getServiceOptions={this.getServiceOptions}
        />
        <ConfirmModal
          id="callConfirmModal"
          title="Delete Call"
          body="Are you sure you want to delete this call?"
          close={closeModal}
          confirm={this.handleDeleteCall}
        />
        <ConfirmModal
          id="closeProjectModal"
          title="Project Ready To Close"
          confirmLabel="OK"
          body={confirmCloseProject}
          showCancel={false}
          confirm={this.handleCloseProjectClick}
          close={closeModal}
        />
        <ProjectDetailsModal
          project={project}
          handleCancel={this.closeProjectDetails}
          handleClose={closeModal}
        />
      </React.Fragment>
    );
  }
}

TimesheetForm.propTypes = {
  onSave: PropTypes.func,
  customers: PropTypes.array,
  auth: PropTypes.object,
  codes: PropTypes.array,
  calls: PropTypes.array,
  dates: PropTypes.array,
  services: PropTypes.array,
  users: PropTypes.array,
  user: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  projects: PropTypes.array,
  contracts: PropTypes.array,
  closeModal: PropTypes.func,
  onEnterCallDetails: PropTypes.func,
  onShowProjectDetails: PropTypes.func,
  deleteCall: PropTypes.func,
  showModalSuccess: PropTypes.func,
  updateCall: PropTypes.func
};

export default TimesheetForm;
